import styled, { useTheme } from 'styled-components'
import { useMediaQuery } from 'styled-breakpoints/use-media-query'
import { Canvas, useFrame, extend } from '@react-three/fiber'
import { ContactShadows, PerspectiveCamera } from '@react-three/drei'
import { Vector3, PlaneGeometry } from 'three'
import { EffectComposer, N8AO } from '@react-three/postprocessing'
import { useControls } from 'leva'
import { Tree } from './Tree'
import { AnimatedEnvironment } from './AnimatedEnvironment'
import useScrollStore from '../useScrollStore'
import { Overlay } from './Overlay'
import { useEffect, useRef } from 'react'
extend({ PlaneGeometry })

// Instanced mesh
// https://threejs.org/examples/?q=gpgpu#webgl_gpgpu_birds_gltf

// GLTFJSX
// https://www.google.com/search?q=r3f+action+play&rlz=1C5GCEM_enCH1049CH1049&biw=1448&bih=775&tbm=vid&ei=JXiyZKdTxayL6A_U74pY&ved=0ahUKEwjn-YrOw5CAAxVF1gIHHdS3AgsQ4dUDCA0&uact=5&oq=r3f+action+play&gs_lp=Eg1nd3Mtd2l6LXZpZGVvIg9yM2YgYWN0aW9uIHBsYXkyCBAhGKABGMMEMggQIRigARjDBEiXFFCQDVjdEXACeACQAQCYAWKgAcgDqgEBNrgBA8gBAPgBAcICCBAAGAcYHhgPwgIIEAAYHhgNGA_CAggQABgWGB4YD8ICBhAAGAcYHsICCBAAGAgYBxgeiAYB&sclient=gws-wiz-video#fpstate=ive&vld=cid:f2dae03b,vid:q7yH_ajINpA

const Camera = () => {
  const { breakpoints } = useTheme()
  const isMobile = useMediaQuery(breakpoints.down('md'))
  const cameraIntialPosition = new Vector3(0, 10, isMobile ? 90 : 70)
  const cameraFinalPosition = new Vector3(0, 10, 100)
  const cameraPct = useRef(0)

  useEffect(
    () =>
      // We want to move the camera when the user is at the end of the scroll
      useScrollStore.subscribe((state) => {
        if (state.scrollPct > 0.95) {
          cameraPct.current = (state.scrollPct - 0.95) * 20
        } else {
          cameraPct.current = 0
        }
      }),
    []
  )

  const amplitude = 0.5
  const speed = 0.1

  useFrame(({ camera, clock, pointer }) => {
    const elapsedTime = clock.getElapsedTime() * speed

    // We want the tree to be close to the title at the beginning
    const initialPositionZ = -30 * Math.max(0, (300 - window.scrollY) / 300)
    const initialPositionY = -0 * Math.max(0, (300 - window.scrollY) / 300)

    // User interaction
    const userPositionX = pointer.x * 0.1 * Math.PI * 10
    const userPositionY = Math.max(0, (pointer.y + 1) * 0.1) * Math.PI * 10

    // Camera position auto animation
    const cameraPositionX =
      cameraIntialPosition.z * amplitude * Math.sin(elapsedTime)
    const cameraPositionY =
      cameraIntialPosition.y * (1.2 + Math.sin(elapsedTime)) * amplitude +
      cameraPct.current * (cameraFinalPosition.y - cameraIntialPosition.y)
    const cameraPositionZ =
      cameraIntialPosition.z * (1 + 0.1 * Math.cos(elapsedTime)) +
      cameraPct.current * (cameraFinalPosition.z - cameraIntialPosition.z)

    camera.position.x = cameraPositionX + userPositionX
    camera.position.y = initialPositionY + cameraPositionY + userPositionY
    camera.position.z = initialPositionZ + cameraPositionZ

    camera.lookAt(0, 13, 0)
  })

  return (
    <PerspectiveCamera
      makeDefault
      position={cameraIntialPosition}
      fov={30}
      near={0.1}
      far={999999}
    />
  )
}

const ExampleComponent = () => {
  const { envId } = useControls('Seasons', {
    envId: 0,
  })

  console.log('RENDER Canvas')

  return (
    <CanvasContainer>
      <Overlay />
      <Canvas gl={{ antialias: false }} shadows>
        <Camera />
        <EffectComposer disableNormalPass>
          <N8AO aoRadius={10} intensity={4} screenSpaceRadius />
        </EffectComposer>
        <AnimatedEnvironment id={envId} />
        <hemisphereLight intensity={0.65} />
        <ContactShadows
          frames={10}
          blur={2}
          opacity={1}
          scale={60}
          color={'rgb(20, 13,10)'}
        />
        <Tree position={new Vector3(0, 0, 0)} />
      </Canvas>
    </CanvasContainer>
  )
}

const CanvasContainer = styled.div`
  width: 100%;
  height: 100vh;
`

export default ExampleComponent
